/**
 * Client-side module availability — the console's view of the SAME module
 * release decision the server makes. The server injects the enabled set as
 * `window.__AVOS_ENABLED_MODULES__` (an id array, or the string "all") via the
 * SSR document bootstrap; this reads it and answers "is module X enabled?".
 *
 * This is DISPLAY-ONLY: it hides disabled modules from navigation + renders a
 * clean "not available" state. The authoritative enforcement is server-side
 * (route redirect + API 404). If the global is absent (e.g. a host that did not
 * inject it), we fail OPEN on the client (treat all as enabled) because the
 * server still gates — the client must never show MORE than the server allows,
 * and a missing bootstrap only risks showing a nav item whose route/API the
 * server will still block.
 */
import { ALL_MODULE_IDS, type ProductModuleId } from "../modules/registry.js";

/** Read the server-injected enabled set, or null when not present. */
function readEnabledGlobal(): ReadonlySet<ProductModuleId> | null {
  if (typeof window === "undefined") return null;
  const raw = (window as unknown as { __AVOS_ENABLED_MODULES__?: unknown })
    .__AVOS_ENABLED_MODULES__;
  if (raw === "all") return new Set(ALL_MODULE_IDS);
  if (Array.isArray(raw)) {
    return new Set(
      raw.filter((x): x is ProductModuleId =>
        typeof x === "string" && (ALL_MODULE_IDS as readonly string[]).includes(x),
      ),
    );
  }
  return null;
}

/** The enabled module set for the client (defaults to all when uninjected). */
export function enabledModuleSet(): ReadonlySet<ProductModuleId> {
  return readEnabledGlobal() ?? new Set(ALL_MODULE_IDS);
}

/**
 * Console nav keys that are NOT standalone product modules but are OWNED by an
 * existing module's capability (so they ride on that module's enabled flag).
 * Inventory & Rates is a core property-management capability → gated by
 * `properties`, never a separate inventory/rates module or env flag.
 */
const NAV_KEY_OWNER: Readonly<Record<string, ProductModuleId>> = {
  inventory: "properties",
};

/** Is a given module id enabled in this environment (per the server)? */
export function isModuleAvailable(id: string): boolean {
  // A nav key owned by an existing module rides on that module's flag.
  const owner = NAV_KEY_OWNER[id];
  if (owner !== undefined) return enabledModuleSet().has(owner);
  // Unknown/non-product keys (e.g. the "booking" redirect alias) are treated as
  // available; only real registry ids are gated.
  if (!(ALL_MODULE_IDS as readonly string[]).includes(id)) return true;
  return enabledModuleSet().has(id as ProductModuleId);
}
